<template>
  <div>
    <div
      id="validation-errors-box"
      class="validation-errors-box"
      v-if="validationErrors && validationErrors.length > 0"
    >
      <template v-for="error in validationErrors">
        <span class="validation-error" :key="error">
          {{ $t("components.plan_builder." + error) }}
        </span>
      </template>
    </div>
    <div class="with-margin">
      <v-card-text>
        {{
          $t(
            "components.plancreationwizard.you_are_few_steps_away_from_building_a_new_benefits_plan"
          )
        }}
      </v-card-text>
      <v-expansion-panels
        accordion
        focusable
        multiple
        v-model="panels"
        :disabled="loading"
      >
        <v-expansion-panel
          id="plan_creation_step1"
          @change="panelFocus('plan_creation_step1')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.generalSettings
                ? ''
                : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.general_settings") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.configure_plan_general_settings_attributes_and_terms"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <general-settings
                :generalSettings="generalSettings"
                @valid="valid.generalSettings = $event"
                @has-classes="$emit('has-classes', $event)"
                @has-occupations="$emit('has-occupations', $event)"
                @update-classes="
                  classesStr = $event;
                  $emit('update-classes', $event);
                "
                @update-occupations="
                  occupationsStr = $event;
                  $emit('update-occupations', $event);
                "
                @has-beneficiaries="setPoliciesHasBeneficiaries"
                @beneficiary-statement-on-trustfund="
                  setInsuranceCarriersPolicesHasBeneficiariesToFalse
                "
                @trust-fund="$emit('trust-fund', $event)"
                :disabled="loading"
                :planType="planType"
                @load-unique-identifiers="$emit('load-unique-identifiers')"
                :uniqueIdentifiers="uniqueIdentifiers"
              />
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          v-if="generalSettings.PlanSettings.HasPremiums"
          id="plan_creation_step2"
          @change="panelFocus('plan_creation_step2')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.insuranceCarriers
                ? ''
                : 'validation-error-header'
            "
          >
            {{ $t("globals.insurance_carriers") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.configure_plan_insurance_carriers_and_their_information"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <insurance-carriers
                :insuranceCarriers="insuranceCarriers"
                :planInsuranceCarriers="planInsuranceCarriers"
                :planIdentityId="planId"
                @insurance-carriers="insuranceCarriers = $event"
                @plan-insurance-carriers="
                  $emit('change-insurance-carriers', $event)
                "
                :planEffectiveDate="generalSettings.PlanSettings.EffectiveDate"
                :hasBeneficiaries="
                  generalSettings.PlanSettings.HasBeneficiaries &&
                  !generalSettings.PlanSettings.BeneficiaryStatementOnFund
                "
                :disabled="loading"
              ></insurance-carriers>
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          id="plan_creation_step3"
          v-if="generalSettings.PlanSettings.IncludeTrustFund"
          @change="panelFocus('plan_creation_step3')"
        >
          <v-expansion-panel-header
            :class="
              !validated || (valid.trustFund && bankAccountValid)
                ? ''
                : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.trust_fund") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.select_and_or_add_plan_trust_fund_and_their_information"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <trust-fund
                ref="trustFund"
                :trustFund="trustFund"
                :planIdentityId="planId"
                @valid="valid.trustFund = $event"
                :disabled="loading"
                @trust-fund="$emit('trust-fund', $event)"
                :planEffectiveDate="generalSettings.PlanSettings.EffectiveDate"
                :hasBeneficiaries="
                  generalSettings.PlanSettings.HasBeneficiaries
                "
                :bankAccount="bankAccount"
                @bank-account="$emit('bank-account', $event)"
              />
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          id="plan_creation_step4"
          @change="panelFocus('plan_creation_step4')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.employers ? '' : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.employers_&_stores") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.add_employers_and_stores_and_their_information"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <employers
                ref="inMemoryEmployers"
                :employers="employers"
                @change-employers="changeEmployers"
                :planIdentityId="planId"
                :hasBillingImport="
                  generalSettings.PlanSettings.HasBillingImport
                "
                :disabled="loading"
                :classesArray="classesArray"
                :hasClasses="hasClasses"
                :hasHSA="generalSettings.PlanSettings.HasHealthSpendingAccount"
                :hasBenefitYears="generalSettings.PlanSettings.HasBenefitYears"
              ></employers>
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          id="plan_creation_step5"
          @change="panelFocus('plan_creation_step5')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.memberFields ? '' : 'validation-error-header'
            "
          >
            {{
              $t("components.plancreationwizard.members_data_fields_&_flags")
            }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.select_members_data_fields_and_add_and_configure_flags_for_member_management"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <member-fields
                :selectedFields="selectedFields"
                :unselectedFields="unselectedFields"
                @update-selection="changeMemberFields"
                :disabled="loading"
              />
              <hour-import-member-identifiers
                v-if="generalSettings.TrackHours"
                :primaryIdentifier="primaryIdentifier"
                :candidateIdentifiers="candidateIdentifiers"
                :includeContributionAmount="includeContributionAmount"
                @update-primary-identifier="
                  $emit('update-primary-identifier', $event)
                "
                @update-candidate-identifiers="
                  $emit('update-candidate-identifiers', $event)
                "
                @update-include-contribution-amount="
                  $emit('update-include-contribution-amount', $event)
                "
                :selectedFields="selectedFields"
                :disabled="loading"
              />
              <flags
                :planIdentityId="planId"
                :flags="flags"
                @update-flags="$emit('change-flags', $event)"
                :disabled="loading"
              />
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          v-if="generalSettings.PlanSettings.HasBilling"
          id="plan_creation_step6"
          @change="panelFocus('plan_creation_step6')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.billingImport ? '' : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.billing_import") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                generalSettings.PlanSettings.HasBillingImport
                  ? $t("components.plancreationwizard.configure_billing_import")
                  : $t("components.plancreationwizard.no_billing_import")
              }}
            </v-card-subtitle>
            <v-card-text>
              <v-container fluid grid-list-md>
                <billing-import
                  :selectedFields="billingFields"
                  :unselectedFields="unselectedBillingFields"
                  @update-selection="$emit('change-billing-fields', $event)"
                  :effectiveDate="generalSettings.PlanSettings.EffectiveDate"
                  @update-billing-term="$emit('change-billing-term', $event)"
                  :billingTerm="billingTerm"
                  :employers="employers"
                  :hasBillingImport="
                    generalSettings.PlanSettings.HasBillingImport
                  "
                  :disabled="loading"
                  @update-contribution-categories="
                    $emit('change-contribution-categories', $event)
                  "
                  :contributionCategories="contributionCategories"
                  :hasStores="generalSettings.PlanSettings.HasStores"
                />
              </v-container>
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          id="plan_creation_step7"
          @change="panelFocus('plan_creation_step7')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.contacts ? '' : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.internal_contacts") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.add_internal_contacts_for_broker"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <v-container fluid>
                <contacts
                  ref="internalContactsTable"
                  @contacts-changed="$emit('change-contacts', $event)"
                  :contacts="internalContacts"
                  identifier="internalContacts"
                  :planIdentityId="planId"
                  :disabled="loading"
                ></contacts>
              </v-container>
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          id="plan_creation_step8"
          @change="panelFocus('plan_creation_step8')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.coverage ? '' : 'validation-error-header'
            "
          >
            {{ $t("globals.coverage") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t("components.plancreationwizard.add_and_configure_coverage")
              }}
            </v-card-subtitle>
            <v-card-text>
              <v-container fluid>
                <coverage
                  :coverage="coverage"
                  @update-coverage="$emit('change-coverage', $event)"
                  :hasPremiums="generalSettings.PlanSettings.HasPremiums"
                  :hasRemittances="generalSettings.PlanSettings.HasRemittances"
                  :effectiveDate="generalSettings.PlanSettings.EffectiveDate"
                  :hasBillingImport="
                    generalSettings.PlanSettings.HasBillingImport
                  "
                  :hasAdminFees="generalSettings.PlanSettings.HasAdminFees"
                  :adminFeesPerTerm="
                    generalSettings.PlanSettings.AdminFeesPerTerm
                  "
                  :hasOperatingFees="
                    generalSettings.PlanSettings.HasOperatingFees
                  "
                  :hasFlatAdminFees="
                    generalSettings.PlanSettings.HasPlanFlatAdminFees
                  "
                  :hasClaimAdminFees="
                    generalSettings.PlanSettings.HasPlanClaimAdminFees
                  "
                  :operatingFeesPerTerm="
                    generalSettings.PlanSettings.OperatingFeesPerTerm
                  "
                  :trustFund="trustFund"
                  :planInsuranceCarriers="planInsuranceCarriers"
                  :disabled="loading"
                  :brokers="brokers"
                />
              </v-container>
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          id="plan_creation_step9"
          @change="panelFocus('plan_creation_step9')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.coverageParameters
                ? ''
                : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.coverage_configuration") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.create_coverage_configuration_parameters"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <v-container fluid>
                <coverage-parameters
                  :planIdentityId="planId"
                  :coverageParameters="coverageParameters"
                  @update-coverage-parameters="
                    $emit('change-coverage-parameters', $event)
                  "
                  :coverage="coverage"
                  :operatingFeesPerTerm="
                    generalSettings.PlanSettings.OperatingFeesPerTerm
                  "
                  :adminFeesPerTerm="
                    generalSettings.PlanSettings.AdminFeesPerTerm
                  "
                  :disabled="loading"
                  :contributionCategories="contributionCategories"
                  :employers="employers"
                  :planType="planType"
                  :calculationEntitiesFormulaTypes="
                    calculationEntitiesFormulaTypes
                  "
                />
              </v-container>
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          id="plan_creation_step10"
          @change="panelFocus('plan_creation_step10')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.formulas ? '' : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.calculation_formulas") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.configure_and_validate_formulas"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <formulas
                ref="formulas"
                :formulas="formulas"
                :coverage="
                  coverage ? coverage.filter((o) => !o.IsFreeOfCharge) : []
                "
                :hasAdminFees="generalSettings.PlanSettings.HasAdminFees"
                :hasOperatingFees="
                  generalSettings.PlanSettings.HasOperatingFees
                "
                :hasContributions="
                  generalSettings.PlanSettings.HasContributions
                "
                :hasFlatAdminFees="
                  generalSettings.PlanSettings.HasPlanFlatAdminFees
                "
                :hasClaimAdminFees="
                  generalSettings.PlanSettings.HasPlanClaimAdminFees
                "
                :adminFeesPerTerm="
                  generalSettings.PlanSettings.AdminFeesPerTerm
                "
                :operatingFeesPerTerm="
                  generalSettings.PlanSettings.OperatingFeesPerTerm
                "
                :planIdentityId="planId"
                @update-formulas="$emit('change-formulas', $event)"
                :parameters="coverageParameters"
                :fields="selectedFields.map((o) => o.Key)"
                :trackHours="generalSettings.TrackHours"
                :hasStores="generalSettings.PlanSettings.HasStores"
                :disabled="loading"
                :hasEmployerContributionsPerTerm="
                  generalSettings.PlanSettings.HasEmployerContributionsPerTerm
                "
                :planVariables="planVariables"
                :taxParameters="taxParameters"
                :claimParameters="claimParameters"
              />
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          id="plan_creation_step11"
          @change="panelFocus('plan_creation_step11')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.memberGroups ? '' : 'validation-error-header'
            "
          >
            {{ $t("globals.member_groups") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.create_and_configure_member_groups"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <v-container fluid>
                <member-groups
                  ref="memberGroupsTable"
                  :memberGroups="memberGroups"
                  :coverage="coverage"
                  identifier="inMemoryMemberGroups"
                  :planIdentityId="planId"
                  @update-member-groups="changeMemberGroups"
                  :disabled="loading"
                  :employers="employers"
                ></member-groups>
              </v-container>
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          v-if="generalSettings.HasEligibility || generalSettings.HasEligibilityBank"
          id="plan_creation_step12"
          @change="eligibilityFocus"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.eligibility ? '' : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.eligibility") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{ $t("components.plancreationwizard.configure_eligibility") }}
            </v-card-subtitle>
            <v-card-text>
              <eligibility
                :sickPaymentBank="
                  generalSettings.PlanSettings.HasSickPaymentBank
                "
                :hasClasses="hasClasses"
                :classesArray="classesArray"
                :effectiveDate="generalSettings.PlanSettings.EffectiveDate"
                :coverage="coverage"
                :memberGroups="memberGroups"
                :selectedFields="selectedFields"
                :eligibility="eligibility"
                @update-activity-status="$emit('activity-status-rules', $event)"
                @update-benefit-rules="$emit('benefit-rules', $event)"
                @update-eligibility-period-rules="
                  $emit('eligibility-period-rules', $event)
                "
                @valid="valid.eligibility = $event"
                :planIdentityId="planId"
                :disabled="loading"
                :employers="employers"
                :logicalExpressionsHeaders="logicalExpressionsHeaders"
                :logicalExpressionsFields="logicalExpressionsFields"
                @update-plan-coverage-commencement-rules="
                  $emit('plan-coverage-commencement-rules', $event)
                "
                @update-member-coverage-eligibility-rules="
                  $emit('member-coverage-eligibility-rules', $event)
                "
                :memberLogicalExpressionsHeaders="
                  memberLogicalExpressionsHeaders
                "
                :memberLogicalExpressionsFields="memberLogicalExpressionsFields"
                :generalSettings="generalSettings"
                :eligibilityHourBankSettings="eligibilityHourBankSettings"
                @update-eligibility-hour-bank-settings="changeEligibilityHourBankSettings"
                :selfPaymentSettings="selfPaymentSettings"
                @update-self-payment-settings="changeSelfPaymentSettings"
              />
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <!-- <v-expansion-panel
          v-if="generalSettings.HasEligibilityBank"
          id="plan_creation_step15"
          @change="panelFocus('plan_creation_step15')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.eligibilityBank ? '' : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.eligibility_bank") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.create_and_configure_eligibility_bank"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <v-container fluid>
                <eligibility-hour-bank-settings
                  ref="eligibilityBankSettingsTable"
                  :memberGroups="memberGroups"
                  identifier="eligibilityBankSettingsTable"
                  :planIdentityId="planId"
                  :eligibilityHourBankSettings="eligibilityHourBankSettings"
                  @update-eligibility-settings="changeEligibilityHourBankSettings"
                  :disabled="loading"
                  :generalSettings="generalSettings"
                ></eligibility-hour-bank-settings>
              </v-container>
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel> -->
        <v-expansion-panel
          id="plan_creation_step13"
          @change="panelFocus('plan_creation_step13')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.reports ? '' : 'validation-error-header'
            "
          >
            {{
              $t("components.plancreationwizard.premiums_and_billing_reports")
            }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.configure_premiums_and_billing_reports"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <reports
                :defaultDataGrouping="defaultDataGrouping"
                :additionalDataGrouping="additionalDataGrouping"
                :reportConfiguration="reportConfiguration"
                :hasBilling="generalSettings.PlanSettings.HasBilling"
                :hasClasses="hasClasses"
                :coverageGroups="coverageGroups"
                @update-coverage-groups="$emit('coverage-groups', $event)"
                @valid="valid.reports = $event"
                @update-default="$emit('default-data-grouping', $event)"
                @update-additional="$emit('additional-data-grouping', $event)"
                :coverage="coverage"
                :reportConfigurationAddress="reportConfigurationAddress"
                @update-reports="$emit('report-configuration-address', $event)" :disabled="loading"
                :planInsuranceCarriers="planInsuranceCarriers"
                @plan-insurance-carriers="$emit('change-insurance-carriers', $event)" />
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          v-if="generalSettings.PlanSettings.HasDemographics"
          id="plan_creation_step14"
          @change="panelFocus('plan_creation_step14')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.demographics ? '' : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.demographics_reports") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.configure_demographics_reports"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <demographics
                :selectedFields="demographicsFields"
                :unselectedFields="unselectedDemographics"
                @update-selection="$emit('change-demographics', $event)"
                :disabled="loading"
              />
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-snackbar
        v-model="snackbar"
        :color="color"
        :multi-line="mode === 'multi-line'"
        :timeout="timeout"
        :vertical="mode === 'vertical'"
      >
        {{ message }}
        <v-btn dark text @click="snackbar = false">
          {{ $t("globals.close") }}
        </v-btn>
      </v-snackbar>
      <error-handler :error="error"></error-handler>
    </div>
  </div>
</template>

<script>
import ErrorHandler from "@/components/ErrorHandler";
import { locker } from "@/mixins/locker";
import Contacts from "@/components/PlanBuilder/Contacts";
import { mapGetters, mapActions } from "vuex";
import { tokens, mask } from "vue-the-mask";
import masker from "vue-the-mask/src/masker";
import GeneralSettings from "@/components/PlanBuilder/GeneralSettings.vue";
import TrustFund from "@/components/PlanBuilder/TrustFund.vue";
import InsuranceCarriers from "@/components/PlanBuilder/InsuranceCarriers.vue";
import Employers from "@/components/PlanBuilder/Employers.vue";
import MemberFields from "@/components/PlanBuilder/MemberFields.vue";
import Flags from "@/components/PlanBuilder/Flags.vue";
import BillingImport from "@/components/PlanBuilder/BillingImport.vue";
import Coverage from "@/components/PlanBuilder/Coverage.vue";
import CoverageParameters from "@/components/PlanBuilder/CoverageParameters.vue";
import Formulas from "@/components/PlanBuilder/Formulas.vue";
import MemberGroups from "@/components/PlanBuilder/MemberGroups.vue";
import Eligibility from "@/components/PlanBuilder/Eligibility.vue";
import Reports from "@/components/PlanBuilder/Reports.vue";
import Demographics from "@/components/PlanBuilder/Demographics.vue";
import HourImportMemberIdentifiers from "@/components/PlanBuilder/HourImportMemberIdentifiers.vue";
import EligibilityHourBankSettings from "@/components/PlanBuilder/EligibilityHourBankSettings.vue";

export default {
  components: {
    ErrorHandler,
    Contacts,
    GeneralSettings,
    TrustFund,
    InsuranceCarriers,
    Employers,
    MemberFields,
    Flags,
    BillingImport,
    Coverage,
    CoverageParameters,
    Formulas,
    MemberGroups,
    Eligibility,
    Reports,
    Demographics,
    HourImportMemberIdentifiers,
    EligibilityHourBankSettings
  },
  mixins: [locker],
  directives: { mask },
  props: [
    "generalSettings",
    "planInsuranceCarriers",
    "trustFund",
    "planId",
    "employers",
    "flags",
    "selectedFields",
    "internalContacts",
    "coverage",
    "coverageParameters",
    "formulas",
    "memberGroups",
    "reportConfiguration",
    "demographicsFields",
    "eligibility",
    "billingFields",
    "billingTerm",
    "contributionCategories",
    "classesArray",
    "reportConfigurationAddress",
    "defaultDataGrouping",
    "coverageGroups",
    "validationErrors",
    "panelsWithErrors",
    "additionalDataGrouping",
    "validated",
    "primaryIdentifier",
    "candidateIdentifiers",
    "includeContributionAmount",
    "logicalExpressionsHeaders",
    "logicalExpressionsFields",
    "memberLogicalExpressionsHeaders",
    "memberLogicalExpressionsFields",
    "planType",
    "bankAccount",
    "taxParameters",
    "planVariables",
    "memberFields",
    "calculationEntitiesFormulaTypes",
    "allDemographicsFields",
    "allBillingImportFields",
    "insuranceCarriers",
    "uniqueIdentifiers",
    "hasClasses",
    "hasOccupations",
    "brokers",
    "claimParameters",
    "eligibilityHourBankSettings",
    "selfPaymentSettings"
  ],
  data() {
    return {
      panels: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13],
      // panels: [],
      postalCodeMask: "A#A #A#",
      telephoneMask: "(###) ### - ####",
      systemEffectiveDate: null,
      // rules: {
      //   phone: (v) => {
      //     let val = this.unmask(v, this.telephoneMask);
      //     return !val || this.$rules.phone(val);
      //   },
      // },
      unselectedFields: [],
      unselectedBillingFields: [],
      unselectedDemographics: [],
      classesStr: "",
      occupationsStr: "",
      valid: {
        generalSettings: true,
        insuranceCarriers: true,
        trustFund: true,
        employers: true,
        memberFields: true,
        billingImport: true,
        contacts: true,
        coverage: true,
        coverageParameters: true,
        formulas: true,
        memberGroups: true,
        eligibility: true,
        reports: true,
        demographics: true,
        bankAccount: true,
        eligibilityBank: true,
      },
      loaded: false,
      error: null,
      snackbar: false,
      color: "",
      mode: "",
      timeout: 2500,
      message: "",
      loading: false,
      bankAccountValid: true,
    };
  },
  computed: {
    ...mapGetters("plansMembers", ["options"]),
  },
  watch: {
    "generalSettings.TrackHours": {
      handler(v) {
        if (!v) {
          this.$emit("update-primary-identifier", undefined);
          this.$emit("update-candidate-identifiers", []);
        }
      },
    },
    // demographicsFields: {
    //   handler(v) {
    //     this.$emit("change-demographics", this.demographicsFields);
    //     // this.selectedDemographics = this.demographicsFields;
    //   },
    //   immediate: true,
    // },
    billingFields: {
      handler(v) {
        if (v !== null && v !== undefined) {
          this.unselectedBillingFields = this.allBillingImportFields.filter(
            (o) => v.filter((d) => d.Key === o.Key).length === 0
          );
        }
      },
      immediate: true,
    },
    valid: {
      handler(v) {
        this.validate();
      },
      immediate: true,
      deep: true,
    },
    // "valid.generalSettings": {
    //   handler(v) {
    //     this.validate();
    //   },
    //   immediate: true,
    // },
    // "valid.reports": {
    //   handler(v) {
    //     this.validate();
    //   },
    //   immediate: true,
    // },
    // "valid.trustFund": {
    //   handler(v) {
    //     this.validate();
    //   },
    //   immediate: true,
    // },
    // "valid.eligibility": {
    //   handler(v) {
    //     this.validate();
    //   },
    //   immediate: true,
    // },
    additionalDataGrouping: {
      handler(v) {
        if (v !== "Coverage Groups") {
          this.$emit("coverage-groups", []);
        }
        // this.validate();
      },
      immediate: true,
    },
    // coverageGroups: {
    //   handler(v) {
    //     this.validate();
    //   },
    //   immediate: true,
    //   deep: true,
    // },
    generalSettings: {
      handler(v) {
        if (v && v.PlanSettings) {
          this.systemEffectiveDate = v.PlanSettings.EffectiveDate;
        }
      },
    },
    classesStr: {
      handler(v) {
        const classes =
          "[" +
          v
            .split(",")
            .map((o) => o.trim())
            .filter((o) => o !== "")
            .map((o) => '"' + o + '"')
            .join(",") +
          "]";
        this.$emit("classes-array", JSON.parse(classes));
      },
    },
    memberFields: {
      handler(v) {
        if (v) {
          this.unselectedFields = v
            .filter(
              (o) =>
                this.selectedFields.filter((f) => f.Key === o.Key).length === 0
            )
            .sort(this.compareFields);
        }
      },
    },
    allDemographicsFields: {
      handler(v) {
        this.unselectedDemographics = v.filter(
          (o) =>
            this.demographicsFields.filter((d) => d.Key === o.Key).length === 0
        );
      },
    },
    allBillingImportFields: {
      handler(v) {
        this.unselectedBillingFields = this.allBillingImportFields.filter(
          (o) => this.billingFields.filter((d) => d.Key === o.Key).length === 0
        );
      },
    },
    hasClasses: {
      handler(v) {
        console.log(
          "###################################################### has classes coverage is",
          JSON.stringify(this.coverage)
        );
        this.computeUnselectedMemberFields();
      },
    },
    hasOccupations: {
      handler(v) {
        this.computeUnselectedMemberFields();
      },
    },
    coverage: {
      handler(v) {
        console.log(
          "###################################################### coverage is",
          JSON.stringify(this.coverage)
        );
      },
    },
  },
  async mounted() {
    console.log("$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$", this.selectedFields);
    console.log(JSON.stringify(this.formulas));
    if (this.generalSettings && this.generalSettings.PlanSettings) {
      this.systemEffectiveDate =
        this.generalSettings.PlanSettings.EffectiveDate;
    }
    if (this.memberFields && this.selectedFields) {
      this.unselectedFields = this.memberFields
        .filter(
          (o) => this.selectedFields.filter((f) => f.Key === o.Key).length === 0
        )
        .sort(this.compareFields);
    }
    if (this.allDemographicsFields && this.demographicsFields) {
      this.unselectedDemographics = this.allDemographicsFields.filter(
        (o) =>
          this.demographicsFields.filter((d) => d.Key === o.Key).length === 0
      );
    }
    if (this.allBillingImportFields && this.billingFields) {
      this.unselectedBillingFields = this.allBillingImportFields.filter(
        (o) => this.billingFields.filter((d) => d.Key === o.Key).length === 0
      );
    }
    this.$nextTick(() => {
      this.panels = [];
    });
  },
  methods: {
    ...mapActions("plansMembers", [
      "getDemographicsFields",
      "getBillingImportFields",
      "validateBankAccount",
    ]),
    computeUnselectedMemberFields() {
      // let memberFields = JSON.parse(JSON.stringify(this.memberFields));

      // if (!this.hasClasses) {
      //   memberFields = memberFields.filter((o) => o.Key !== "Class");
      // } else {
      //   if (
      //     this.selectedFields.filter((o) => o.Key === "Class").length === 0
      //   ) {
      //     let field = memberFields.filter((o) => o.Key === "Class")[0];
      //     if (field !== undefined && field !== null) {
      //       this.selectedFields.push(field);
      //     }
      //   }
      // }
      // if (!this.hasOccupations) {
      //   memberFields = memberFields.filter((o) => o.Key !== "Occupation");
      // } else {
      //   if (
      //     this.selectedFields.filter((o) => o.Key === "Occupation").length ===
      //     0
      //   ) {
      //     let field = memberFields.filter((o) => o.Key === "Occupation")[0];
      //     this.selectedFields.push(field);
      //   }
      // }
      // console.log(memberFields)
      console.log(this.unselectedFields);
      console.log(this.selectedFields);
      this.unselectedFields = this.memberFields
        .filter((o) => {
          // console.log(o);
          return (
            this.selectedFields.filter((f) => {
              // console.log(f);
              return f.Key === o.Key;
            }).length === 0
          );
        })
        .sort(this.compareFields);
    },
    changeMemberFields(fields) {
      this.$emit("change-fields", fields);
      this.$nextTick(() => {
        this.loadBillingFields();
        this.loadDemographicFields();
      });
    },
    changeMemberGroups(val) {
      // $emit('change-groups', $event)
      this.$emit("change-groups", val);
    },
    changeEligibilityHourBankSettings(val) {
      // $emit('change-groups', $event)
      this.$emit("change-eligibility-hour-bank-settings", val);
    },
    changeSelfPaymentSettings(val) {
      // $emit('change-groups', $event)
      console.log("v is ", JSON.stringify(val));
      this.$emit("change-self-payment-settings", val);
    },
    changeEmployers(val) {
      // $emit('change-employers', $event)
      this.$emit("change-employers", val);
    },
    setPoliciesHasBeneficiaries(val) {
      console.log("Has Beneficiaries changed to: ", val);
      if (!val) {
        this.generalSettings.PlanSettings.BeneficiaryStatementOnFund = false;

        if (this.planInsuranceCarriers.length > 0) {
          this.planInsuranceCarriers.forEach((carrier) => {
            if (carrier.Policy) carrier.Policy.HasBeneficiaries = false;
          });
        }
        if (this.trustFund) {
          if (this.trustFund.Policy) {
            this.trustFund.Policy.HasBeneficiaries = false;
          }
        }
      }
    },
    setInsuranceCarriersPolicesHasBeneficiariesToFalse() {
      if (this.planInsuranceCarriers.length > 0) {
        this.planInsuranceCarriers.forEach((carrier) => {
          if (carrier.Policy) carrier.Policy.HasBeneficiaries = false;
        });
      }
    },
    clearTrustFund(val) {
      console.log("Include Trustfund changed to: ", val);
      if (!val) {
        this.trustFund = {};
      }
    },
    scrollToValidationErrors() {
      // document.getElementById("validation-errors-box").scrollIntoView({
      //   behavior: "smooth",
      // });
      // window.scrollTo(0, 0);
      this.validate();
      if (this.validationErrors.length === 0) {
        this.color = "success";
        this.message = this.$t("views.planwizardview.validate_success_{0}", [
          this.generalSettings.UniqueIdentifier,
        ]);
        this.snackbar = true;

        this.valid.insuranceCarriers = true;
        this.valid.employers = true;
        this.valid.billingImport = true;
        this.valid.coverage = true;
        this.valid.coverageParameters = true;
        this.valid.formulas = true;
        this.valid.memberGroups = true;
        this.valid.eligibility = true;
        this.valid.demographics = true;
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
        if (this.panelsWithErrors.indexOf("InsuranceCarriers") > -1) {
          this.valid.insuranceCarriers = false;
        } else {
          this.valid.insuranceCarriers = true;
        }
        // if (this.panelsWithErrors.indexOf("Trustfund") > -1) {
        //   this.valid.trustfund = false;
        // } else {
        //   this.valid.trustfund = true;
        // }
        if (this.panelsWithErrors.indexOf("Employers") > -1) {
          this.valid.employers = false;
        } else {
          this.valid.employers = true;
        }
        if (this.panelsWithErrors.indexOf("BillingImport") > -1) {
          this.valid.billingImport = false;
        } else {
          this.valid.billingImport = true;
        }
        if (this.panelsWithErrors.indexOf("Demographics") > -1) {
          this.valid.demographics = false;
        } else {
          this.valid.demographics = true;
        }
        if (this.panelsWithErrors.indexOf("MemberGroups") > -1) {
          this.valid.memberGroups = false;
        } else {
          this.valid.memberGroups = true;
        }
        if (this.panelsWithErrors.indexOf("CoverageParameters") > -1) {
          this.valid.coverageParameters = false;
        } else {
          this.valid.coverageParameters = true;
        }
        if (this.panelsWithErrors.indexOf("Coverage") > -1) {
          this.valid.coverage = false;
        } else {
          this.valid.coverage = true;
        }
        if (this.panelsWithErrors.indexOf("Eligibility") > -1) {
          this.valid.eligibility = false;
        } else {
          this.valid.eligibility = true;
        }
        if (this.panelsWithErrors.indexOf("Formulas") > -1) {
          this.valid.formulas = false;
        } else {
          this.valid.formulas = true;
        }
      }
    },
    async validate() {
      if (this.validated) {
        this.panels = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
        if (this.$refs.trustFund) {
          this.$refs.trustFund.isBankAccountValid();
        }
        // this.$nextTick(() => {
        // this.panels = [];
        this.bankAccountValid = true;
        if (this.bankAccount) {
          for (let key of Object.keys(this.bankAccount)) {
            if (
              this.bankAccount[key] === null ||
              this.bankAccount[key] === ""
            ) {
              delete this.bankAccount[key];
            }
          }
          if (Object.keys(this.bankAccount).length > 0) {
            if (Object.keys(this.bankAccount).length === 3) {
              this.bankAccountValid = (
                await this.validateBankAccount({
                  payload: {
                    InstitutionNumber: this.bankAccount.InstitutionNumber,
                    TransitNumber: this.bankAccount.TransitNumber,
                  },
                })
              ).data;
            } else {
              this.bankAccountValid = false;
            }
          }
        }
        const valid =
          this.valid.generalSettings &&
          this.valid.trustFund &&
          this.valid.eligibility &&
          this.valid.reports &&
          this.bankAccountValid;
        console.log(
          "valid change: ",
          this.valid.generalSettings,
          this.valid.trustFund,
          this.valid.eligibility,
          this.valid.reports,
          this.bankAccountValid
        );
        this.$emit("valid-changed", valid);
        let index = this.validationErrors.indexOf(
          "components.plan_builder.client_validation_errors"
        );
        if (valid) {
          if (index > -1) {
            this.validationErrors.splice(index, 1);
          }
        } else {
          if (index < 0) {
            this.validationErrors.unshift(
              "components.plan_builder.client_validation_errors"
            );
          }
        }
        // });
      }
    },
    async loadDemographicFields() {
      console.log(
        "*************************************************************************************"
      );
      this.loading = true;
      try {
        let allDemographicsFields = (
          await this.getDemographicsFields({
            planIdentityId: this.planId,
            payload: this.selectedFields.map((o) => o.Key),
          })
        ).data;

        // this.selectedDemographics = this.demographicsFields;
        this.unselectedDemographics = allDemographicsFields.filter(
          (o) =>
            this.demographicsFields.filter((d) => d.Key === o.Key).length === 0
        );
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
        // this.valid.demographicsFunc();
      }
    },
    eligibilityFocus() {
      this.$emit("get-logical-expressions-headers");
      this.$emit("get-member-logical-expressions-headers");
      let panel = document.getElementById("plan_creation_step12");
      if (panel.ariaExpanded === "true") {
        return;
      }
      // let activityStatusRules = [];
      // if (this.eligibility && this.eligibility.ActivityStatusRules) {
      //   for (let rule of this.eligibility.ActivityStatusRules) {
      //     rule.CoverageIdentities = rule.CoverageIdentities.filter(
      //       (o) =>
      //         this.coverage.filter((c) => c.IdentityId === o.IdentityId)
      //           .length > 0
      //     );
      //   }
      //   activityStatusRules = this.eligibility.ActivityStatusRules.filter(
      //     () => true
      //   );
      // }
      // this.$emit("activity-status-rules", activityStatusRules);
      // let benefitRules = [];
      // if (this.eligibility && this.eligibility.BenefitRules) {
      //   benefitRules = this.eligibility.BenefitRules.filter(() => true);
      // }
      // this.$emit("benefit-rules", benefitRules);

      // let eligibilityPeriodRules = [];
      // if (this.eligibility && this.eligibility.EligibilityPeriodRules) {
      //   eligibilityPeriodRules = this.eligibility.EligibilityPeriodRules.filter(
      //     () => true
      //   );
      // }
      // this.$emit("eligibility-period-rules", eligibilityPeriodRules);

      // let planCoverageCommencementRules = [];
      // if (this.eligibility && this.eligibility.CoverageCommencementRules) {
      //   planCoverageCommencementRules = this.eligibility.CoverageCommencementRules.filter(
      //     () => true
      //   );
      // }
      // this.$emit(
      //   "plan-coverage-commencement-rules",
      //   planCoverageCommencementRules
      // );

      setTimeout(() => {
        document.getElementById("plan_creation_step12").scrollIntoView({
          behavior: "smooth",
        });
      }, 450);
    },
    async loadBillingFields() {
      try {
        this.loading = true;
        let allBillingImportFields = (
          await this.getBillingImportFields({
            planIdentityId: this.planId,
            payload: this.selectedFields.map((o) => o.Key),
          })
        ).data;

        for (let field of this.billingFields) {
          field.Label = field.Label.replace(" (optional)", "");
          field.Label = field.Label.replace(" (required)", "");
          if (field.IsOptional) {
            field.Label = field.Label + " (optional)";
          } else {
            field.Label = field.Label + " (required)";
          }
        }
        this.unselectedBillingFields = allBillingImportFields.filter(
          (o) => this.billingFields.filter((d) => d.Key === o.Key).length === 0
        );
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
        // this.validate();
      }
    },
    panelFocus(panelId) {
      let panel = document.getElementById(panelId);
      if (panel.ariaExpanded === "true") {
        return;
      }
      setTimeout(() => {
        document.getElementById(panelId).scrollIntoView({
          behavior: "smooth",
        });
      }, 450);
    },
    unmask(v, mask) {
      if (v) {
        return masker(v, mask, false, tokens);
      }
      return v;
    },
    compareFields(a, b) {
      if (a.Label < b.Label) {
        return -1;
      }
      if (a.Label > b.Label) {
        return 1;
      }
      return 0;
    },
    getUsedParameters(code) {
      const res = [];
      const content = code;
      const exp = /\[[a-zA-Z0-9\.]*\]/g;
      let match;
      match = exp.exec(content);
      while (match !== null) {
        res.push(match[0]);
        match = exp.exec(content);
      }
      return res;
    },
    getTranslatedField(translations, key) {
      if (!translations) throw new Error("no translations");
      for (let row of translations) {
        if (!Object.keys(row).indexOf(key) < 0) return "";
        return row[key];
      }
      throw new Error("no translations");
    },
    convertToCamelCase(input) {
      return this.toCamelCase(this.toWords(input));
    },
    toWords(input) {
      var regex =
        /[A-Z\xC0-\xD6\xD8-\xDE]?[a-z\xDF-\xF6\xF8-\xFF]+|[A-Z\xC0-\xD6\xD8-\xDE]+(?![a-z\xDF-\xF6\xF8-\xFF])|\d+/g;

      return input.match(regex);
    },
    toCamelCase(inputArray) {
      let result = "";

      for (let i = 0, len = inputArray.length; i < len; i++) {
        let currentStr = inputArray[i];

        let tempStr = currentStr.toLowerCase();

        if (i != 0) {
          // convert first letter to upper case (the word is in lowercase)
          tempStr = tempStr.substr(0, 1).toUpperCase() + tempStr.substr(1);
        }

        result += tempStr;
      }

      return result;
    },
  },
};
</script>
<style scoped>
.validation-error {
  color: #ff5252;
  font-size: 14px;
  display: block;
}

.validation-errors-box {
  background-color: whitesmoke;
  margin: 20px;
}

.validation-error-header {
  background-color: #ff5252;
  color: white;
}
</style>
